import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CalendarOff, Trash2, Plus } from "lucide-react";

const fmt = (d) => new Date(d).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });

export default function LeaveDays({ doctorId }) {
  const [leaves, setLeaves] = useState([]);
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");

  const load = useCallback(() => api.get("/leaves", { params: doctorId ? { doctor_id: doctorId } : {} }).then((r) => setLeaves(r.data)).catch(() => {}), [doctorId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!date) return toast.error("Pick a date");
    try { await api.post("/leaves", { doctor_id: doctorId, date, reason }); toast.success("Day off added"); setDate(""); setReason(""); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/leaves/${id}`); toast.success("Day off removed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4" data-testid="leave-days">
      <h3 className="font-head font-semibold flex items-center gap-2"><CalendarOff className="h-4 w-4 text-teal-600" /> Days Off</h3>
      <p className="text-xs text-slate-500">Staff cannot book appointments on these dates.</p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1"><Label>Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="leave-date-input" /></div>
        <div className="space-y-1 flex-1 min-w-[180px]"><Label>Reason (optional)</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Conference, personal…" data-testid="leave-reason-input" /></div>
        <Button onClick={add} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="add-leave-button"><Plus className="h-4 w-4 mr-1" /> Add</Button>
      </div>
      {leaves.length === 0 ? <div className="text-sm text-slate-400 py-4 text-center">No upcoming days off.</div> : (
        <ul className="divide-y" data-testid="leave-list">
          {leaves.map((l) => (
            <li key={l.id} className="flex items-center justify-between py-2.5 text-sm" data-testid="leave-item">
              <div><span className="font-medium text-slate-900">{fmt(l.date)}</span>{l.reason && <span className="text-slate-500"> · {l.reason}</span>}</div>
              <Button size="icon" variant="ghost" className="text-rose-600" onClick={() => remove(l.id)} data-testid="remove-leave-button"><Trash2 className="h-4 w-4" /></Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
